import { FunctionComponent, ReactNode, useState, useCallback, useRef } from "react";

import { Box, Collapse, Stack, Fade } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import { FilterBoxConfig, useFilterBoxStateContext } from "@/components/FilterBox/FilterBoxStateContext";
import { FilterRow } from "@/components/FilterBox/FilterRow";
import { BaseFilterInput, FilterCombinator } from "@/components/FilterBox/types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { SearchFilter } from "@/components/Filters/SearchFilter/SearchFilter";
import { useSearchFilter } from "@/components/Filters/SearchFilter/useSearchFilter";
import { DashboardButton as Button } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { TableToolbar } from "@/dashboard-design-system/DashboardTableCustomization/TableToolbar/TableToolbar";
import { Add, ArrowBackward, Disk, Filtering } from "@/icons/outlined";

export interface QuickFilter {
  type: string;
  label: string;
  trigger?: Parameters<NonNullable<NonNullable<FilterBoxConfig<BaseFilterInput>["amplitude"]>["onFilterClicked"]>>[1];
}

interface FilterBoxProps {
  openedFrom?: string;
  onCreated?: (listId: string) => void;
  actionsSlot?: ReactNode;
  tableActionsSlot?: ReactNode;
  moreFiltersLabel?: string;
  actionsPlacement?: "inline" | "end";
  searchPlaceholder?: string;
  isReservedList?: boolean;
  disabledFiltersTooltip?: string;
  quickFilters?: QuickFilter[];
  maxFilters?: number;
  initiallyShowTypeSelector?: boolean;
  onEmpty?: () => void;
  renderFooter?: (controls: {
    addFilter: () => void;
    resetFilters: () => void;
    isAddingFilter: boolean;
    canAddFilter: boolean;
    saveAsListButton: ReactNode;
  }) => ReactNode;
}

export const TableFilterBuilder: FunctionComponent<FilterBoxProps> = ({
  openedFrom = "",
  onCreated,
  actionsSlot,
  tableActionsSlot,
  moreFiltersLabel,
  actionsPlacement = "end",
  searchPlaceholder,
  isReservedList = false,
  disabledFiltersTooltip,
  quickFilters = [],
  maxFilters = Infinity,
  initiallyShowTypeSelector = false,
  renderFooter,
  onEmpty,
}) => {
  const {
    filters,
    visibleFilters,
    filterCombinator,
    setFilterCombinator,
    areAllFiltersValid,
    searchQuery,
    setSearchQuery,
    features,
    preventSaveAsList,
    renderSaveAsListModal,
    amplitude,
    addFilter,
    setFilters,
  } = useFilterBoxStateContext<BaseFilterInput>();

  const [showingTypeSelector, setShowingTypeSelector] = useState(
    () => initiallyShowTypeSelector && visibleFilters.length === 0 && filters.length < maxFilters
  );
  const [isCreateListModalOpen, setIsCreateListModalOpen] = useState(false);
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  const [pendingRowTrackingId, setPendingRowTrackingId] = useState<string | undefined>(() =>
    showingTypeSelector ? crypto.randomUUID() : undefined
  );
  const rowTrackingIdsRef = useRef<Map<string, string>>(new Map());

  const filterContainerRef = useRef<HTMLDivElement>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery]
  );

  const { onSearchFilterChange, onSearchFilterClear, searchFilterValue } = useSearchFilter({
    defaultValue: searchQuery,
    handleChange: handleSearchChange,
  });

  const handleAddFilter = () => {
    if (filters.length >= maxFilters) {
      return;
    }
    setPendingRowTrackingId(crypto.randomUUID());
    amplitude?.onFilterClicked?.(undefined, "moreFilters");
    setShowingTypeSelector(true);
  };

  const handleQuickFilterClick = (filterType: string, trigger: QuickFilter["trigger"]) => {
    if (filters.length >= maxFilters) {
      return;
    }
    amplitude?.onFilterClicked?.(filterType, trigger);
    addFilter(filterType);
  };

  const handleRowCreated = (newFilterId: string) => {
    if (pendingRowTrackingId) {
      rowTrackingIdsRef.current.set(newFilterId, pendingRowTrackingId);
    }
    setPendingRowTrackingId(undefined);
  };

  const triggerResize = () => {
    if (filterContainerRef.current) {
      const resizeEvent = new Event("resize", { bubbles: true });
      filterContainerRef.current.dispatchEvent(resizeEvent);
    }
  };

  const handleCreateList = () => {
    setIsCreateListModalOpen(true);
    amplitude?.onListStarted?.(
      openedFrom,
      filters.map((filter) => filter.type)
    );
  };

  const handleCloseCreateListModal = () => {
    setIsCreateListModalOpen(false);
  };

  const isSaveAsListBlocked = Boolean(preventSaveAsList?.(visibleFilters));
  const showCreateListButton =
    !isSmallScreen && features?.saveAsList && areAllFiltersValid && visibleFilters.length > 0;
  const saveAsListButton = showCreateListButton && (
    <DashboardTooltip
      title={isSaveAsListBlocked ? t("dashboard", "ContactFiltering.saveAsListDisabledDynamicList") : ""}
      placement="top"
    >
      <span>
        <Button
          startIcon={<Disk />}
          variant="tonal"
          size="small"
          vibe="brand"
          disabled={isSaveAsListBlocked}
          onClick={handleCreateList}
          data-test="table-save-filters-as-list"
        >
          {t("dashboard", "ContactFiltering.saveFiltersAsListButton")}
        </Button>
      </span>
    </DashboardTooltip>
  );

  const searchInput = features?.search && (
    <SearchFilter
      onChange={onSearchFilterChange}
      onClear={onSearchFilterClear}
      searchText={searchFilterValue}
      placeholder={searchPlaceholder ?? t("dashboard", "ContactFiltering.searchPlaceholder")}
      size={renderFooter ? undefined : "small"}
      fullWidth={!renderFooter}
      disableExpansion={!renderFooter}
    />
  );

  const actionsSlotBox = actionsSlot && (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        flexWrap: renderFooter ? undefined : "wrap",
        marginInlineStart: actionsPlacement === "end" ? "auto" : undefined,
      }}
    >
      {actionsSlot}
    </Box>
  );

  const toolbar = (
    <TableToolbar
      data-test="filter-toolbar"
      search={
        searchInput && (
          <Stack
            sx={(theme) => ({
              width: { xs: "100%", sm: theme.spacing(25) },
              minWidth: 0,
              "& .MuiInputBase-root": { height: theme.spacing(4.5) },
            })}
          >
            {searchInput}
          </Stack>
        )
      }
      filters={
        <>
          {!isReservedList && (
            <>
              {quickFilters.map((quickFilter) => (
                <Button
                  key={quickFilter.type}
                  variant="outlined"
                  vibe="neutral"
                  endIcon={<Filtering />}
                  onClick={() => handleQuickFilterClick(quickFilter.type, quickFilter.trigger)}
                  disabled={
                    filters.length >= maxFilters || visibleFilters.some((filter) => filter.type === quickFilter.type)
                  }
                  data-test={`quick-filter-${quickFilter.type}`}
                >
                  {quickFilter.label}
                </Button>
              ))}
              <Button
                endIcon={<Filtering />}
                variant="outlined"
                vibe="neutral"
                onClick={handleAddFilter}
                disabled={showingTypeSelector || filters.length >= maxFilters}
                data-test="more-filters"
              >
                {moreFiltersLabel ?? t("dashboard", "ContactFiltering.moreFilters")}
              </Button>
            </>
          )}
          {actionsPlacement === "inline" && actionsSlotBox}
        </>
      }
      actions={
        (tableActionsSlot || (actionsPlacement === "end" && actionsSlotBox)) && (
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ flexWrap: "wrap" }}
            data-test="table-toolbar-actions"
          >
            {tableActionsSlot}
            {actionsPlacement === "end" && actionsSlotBox}
          </Stack>
        )
      }
    />
  );

  const filterCard = (
    <Fade in={true} timeout={300}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.neutral.supershy,
          padding: 2,
          borderRadius: 1,
          ...(!renderFooter && {
            "& [data-test='filter-row']": {
              gridTemplateColumns: { xs: "minmax(0, 1fr) auto", sm: "1fr 160px 2fr auto" },
            },
          }),
        })}
      >
        <Box>
          {features?.combinator && (
            <Collapse in={visibleFilters.length > 1} timeout={300} unmountOnExit onEntered={triggerResize}>
              <Box sx={{ marginBottom: 1, width: 213 }}>
                <DashboardSelect
                  value={filterCombinator}
                  onChange={(event: SelectChangeEvent<unknown>) => {
                    const newCombinator = event.target.value as FilterCombinator;
                    amplitude?.onFilterCombinatorChanged?.({ combinator: newCombinator });
                    setFilterCombinator(newCombinator);
                  }}
                  size="medium"
                  vibe="neutral"
                  data-test="filter-combinator-dropdown"
                >
                  <DashboardMenuItem value="AND" data-test="filter-combinator-and">
                    {t("dashboard", "ContactFiltering.allFiltersApply")}
                  </DashboardMenuItem>
                  <DashboardMenuItem value="OR" data-test="filter-combinator-or">
                    {t("dashboard", "ContactFiltering.atLeastOneFilterApplies")}
                  </DashboardMenuItem>
                </DashboardSelect>
              </Box>
            </Collapse>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} ref={filterContainerRef}>
            {visibleFilters.map((filter) => (
              <Box key={filter.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FilterRow
                  filterId={filter.id}
                  isReservedList={isReservedList}
                  rowTrackingId={rowTrackingIdsRef.current.get(filter.id) ?? filter.id}
                  onRemove={visibleFilters.length === 1 && !showingTypeSelector ? onEmpty : undefined}
                />
              </Box>
            ))}

            {showingTypeSelector && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FilterRow
                  filterId={undefined}
                  onCancel={() => setShowingTypeSelector(false)}
                  isReservedList={isReservedList}
                  rowTrackingId={pendingRowTrackingId}
                  onRowCreated={handleRowCreated}
                  onRemove={visibleFilters.length === 0 ? onEmpty : undefined}
                />
              </Box>
            )}

            {renderFooter?.({
              addFilter: handleAddFilter,
              resetFilters: () => {
                setFilters([]);
                setShowingTypeSelector(false);
              },
              isAddingFilter: showingTypeSelector,
              canAddFilter: !showingTypeSelector && filters.length < maxFilters,
              saveAsListButton,
            })}
            {!isReservedList && !renderFooter && (
              <Fade in={!showingTypeSelector && areAllFiltersValid} timeout={200}>
                <Box sx={{ marginTop: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddFilter}
                    disabled={filters.length >= maxFilters}
                    vibe="neutral"
                    sx={(theme) => ({ paddingLeft: 1.5, paddingRight: 2, height: theme.spacing(3.75) })}
                  >
                    {t("dashboard", "paymentsPage.table.filters.add")}
                  </Button>
                  {saveAsListButton}
                  <Button
                    variant="text"
                    size="small"
                    vibe="neutral"
                    endIcon={<ArrowBackward />}
                    sx={{ marginInlineStart: "auto" }}
                    data-test="filter-reset"
                    onClick={() => {
                      setFilters(filters.filter((filter) => filter.isMandatory));
                      setFilterCombinator("AND");
                      setShowingTypeSelector(false);
                      onEmpty?.();
                    }}
                  >
                    {t("dashboard", "paymentsPage.table.filters.resetAll")}
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );

  const filterCardSection = isReservedList ? (
    <DashboardTooltip
      title={disabledFiltersTooltip ?? t("dashboard", "contactList.filter.reservedListDisabledTooltip")}
      placement="top"
    >
      <Box sx={{ display: "inline-block", width: "100%", cursor: "not-allowed" }}>
        <Box sx={{ pointerEvents: "none", opacity: 0.5 }}>{filterCard}</Box>
      </Box>
    </DashboardTooltip>
  ) : (
    filterCard
  );

  if (visibleFilters.length === 0 && !showingTypeSelector && !renderFooter) {
    return toolbar;
  }

  return (
    <Stack gap={1}>
      {!renderFooter && toolbar}
      {filterCardSection}
      {renderFooter && searchInput && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {searchInput}
          {actionsSlotBox}
        </Box>
      )}
      {features?.saveAsList &&
        renderSaveAsListModal?.({
          isOpen: isCreateListModalOpen,
          onClose: handleCloseCreateListModal,
          onCreated,
        })}
    </Stack>
  );
};
